import { api } from "@/helper/api";
import { getImageUrl } from "@/helper/imageUrlHandler";
import { LmsCourseCardData, LmsCourseCardTone } from "@/components/lms/lmsCourseCard";
import { getLmsCourseCategoryId } from "@/components/lms/lmsCourseCategoryPresentation";
import {
  formatDuration,
  getLmsCourseFeaturedImage,
  getLmsCourseLanguageLabel,
  getLmsCourseSelectedLanguage,
  getLmsDisplayText,
  getLmsManagerRequestParameters,
  LmsMode,
} from "@/components/lms/lms.utils";

export type LearnerCourseTab = "active" | "completed";
export type ManagerCourseTab = "enrolled" | "all";

export interface PaginatedResponse<T> {
  count: number;
  results: T[];
}

export const PAGE_SIZE = 20;
export const MIN_LMS_COURSE_SEARCH_LENGTH = 3;

export const getAppliedCourseSearchTerm = (searchInput: string): string => {
  const trimmedSearchTerm = searchInput.trim();
  return trimmedSearchTerm.length >= MIN_LMS_COURSE_SEARCH_LENGTH
    ? trimmedSearchTerm
    : "";
};

export const getServerSideCourseNameSearchParams = (
  searchTerm: string,
  paramName:
    | "title__icontains"
    | "course__title__icontains" = "title__icontains"
): Record<string, string> => {
  const appliedSearchTerm = getAppliedCourseSearchTerm(searchTerm);
  if (!appliedSearchTerm) {
    return {};
  }

  return { [paramName]: appliedSearchTerm };
};

export const getManagerCourseListQueryParameters = (
  tab: ManagerCourseTab,
  mode: LmsMode,
  selectedCompanySubjectId?: string | number,
  filters: {
    categoryId?: string;
    language?: string;
    searchTerm?: string;
  } = {}
): Record<string, string | number> => {
  const parameters: Record<string, string | number> = {
    ...getLmsManagerRequestParameters(mode, selectedCompanySubjectId),
    ...(mode === "manager" ? { prefer_translation: "true" } : {}),
    ...getServerSideCourseNameSearchParams(filters.searchTerm ?? ""),
  };

  if (filters.categoryId) {
    parameters.category__id = filters.categoryId;
  }
  if (filters.language) {
    parameters.language = filters.language;
  }

  if (tab === "enrolled") {
    parameters.has_company_enrolments = "true";
  }

  return parameters;
};

export const getLanguageCode = (language?: string | null) => {
  if (!language) {
    return "-";
  }

  const normalizedLanguage = language.trim().toLowerCase();

  if (normalizedLanguage.startsWith("de")) {
    return "DE";
  }

  if (normalizedLanguage.startsWith("en")) {
    return "EN";
  }

  return normalizedLanguage.slice(0, 2).toUpperCase();
};

export const getAvailableLanguagesLabel = (
  availableLanguages: string[] | undefined,
  t: (value: string) => string,
  fallbackLanguage?: string | null
) => {
  const languages = availableLanguages?.length
    ? availableLanguages
    : fallbackLanguage
    ? [fallbackLanguage]
    : [];
  return languages.length
    ? languages
        .map((language) => getLmsCourseLanguageLabel(language, t))
        .join(", ")
    : "-";
};

export const getAvailableLanguageCodes = (
  availableLanguages?: string[]
): string[] =>
  Array.from(
    new Set(
      (availableLanguages ?? [])
        .map(getLanguageCode)
        .filter((languageCode) => languageCode !== "-")
    )
  );

const getVariantDurationInMinutes = (variant: any): number | undefined => {
  const duration = Number(variant?.duration_in_minutes);
  return Number.isFinite(duration) ? duration : undefined;
};

export const getAvailableDurationsLabel = (
  course?: any,
  fallbackDuration?: number | string | null
): string => {
  const durationByLanguageCode = new Map<string, number>();

  for (const variant of Array.isArray(course?.variants)
    ? course.variants
    : []) {
    if (variant?.is_active === false) {
      continue;
    }

    const languageCode = getLanguageCode(variant?.language);
    const duration = getVariantDurationInMinutes(variant);
    if (languageCode === "-" || duration == null) {
      continue;
    }

    if (!durationByLanguageCode.has(languageCode)) {
      durationByLanguageCode.set(languageCode, duration);
    }
  }

  const orderedLanguageCodes = getAvailableLanguageCodes(
    course?.available_languages
  );
  const orderedDurations = (
    orderedLanguageCodes.length
      ? orderedLanguageCodes.map((languageCode) =>
          durationByLanguageCode.get(languageCode)
        )
      : Array.from(durationByLanguageCode.values())
  ).filter((duration): duration is number => duration != null);

  if (orderedDurations.length) {
    return Array.from(new Set(orderedDurations.map(formatDuration))).join(", ");
  }

  return formatDuration(course?.duration_in_minutes ?? fallbackDuration);
};

export const appendInProgressStatusProgress = (
  label: string,
  status?: string | null,
  progressPercentage?: number | string | null,
  notYetPassedLabel?: string
): string => {
  if ((status ?? "").trim().toLowerCase() !== "in_progress") {
    return label;
  }

  if (progressPercentage == null || progressPercentage === "") {
    return label;
  }

  const parsedProgress = Number(progressPercentage);
  if (!Number.isFinite(parsedProgress)) {
    return label;
  }

  const roundedProgress = Math.round(
    Math.max(0, Math.min(100, parsedProgress))
  );
  if (roundedProgress >= 100 && notYetPassedLabel) {
    return `${roundedProgress}% ⋅ ${notYetPassedLabel}`;
  }

  return `${label} ⋅ ${roundedProgress}%`;
};

export const getLearnerStatusPresentation = (
  t: (value: string) => string,
  status?: string | null,
  progressPercentage?: number | string | null
): {
  label?: string;
  tone: LmsCourseCardTone;
  tab: LearnerCourseTab;
} => {
  const normalizedStatus = status?.toLowerCase() ?? "";

  if (normalizedStatus === "completed") {
    return {
      label: t("completed"),
      tone: "success",
      tab: "completed",
    };
  }

  if (normalizedStatus === "not_started") {
    return {
      label: t("not_started"),
      tone: "error",
      tab: "active",
    };
  }

  if (normalizedStatus === "in_progress") {
    const baseLabel = t("in_progress");
    return {
      label: appendInProgressStatusProgress(
        baseLabel,
        normalizedStatus,
        progressPercentage,
        t("not_yet_passed")
      ),
      tone: "warning",
      tab: "active",
    };
  }

  return {
    label: normalizedStatus ? status ?? undefined : undefined,
    tone: "neutral",
    tab: "active",
  };
};

export const getLearnerProgressStatusKey = ({
  assignedCount,
  completedCount,
  inProgressCount,
}: {
  assignedCount: number;
  completedCount: number;
  inProgressCount: number;
}):
  | "learner_progress_status_no_courses"
  | "learner_progress_complete"
  | "learner_progress_status_in_progress"
  | "learner_progress_status_not_started" => {
  if (assignedCount <= 0) {
    return "learner_progress_status_no_courses";
  }

  if (completedCount >= assignedCount) {
    return "learner_progress_complete";
  }

  if (inProgressCount > 0 || completedCount > 0) {
    return "learner_progress_status_in_progress";
  }

  return "learner_progress_status_not_started";
};

export const mapLearnerEnrolmentToCourseCardData = (
  enrolment: any,
  t: (value: string, options?: any) => string
): { tab: LearnerCourseTab; course: LmsCourseCardData } => {
  const course =
    enrolment?.course ??
    enrolment?.course_data ??
    enrolment?.course_details ??
    enrolment?.learning_course ??
    null;
  const learnerStatus = getLearnerStatusPresentation(
    t,
    enrolment?.status,
    enrolment?.progress_percentage
  );
  const selectedLanguage = getLmsCourseSelectedLanguage(course);
  const featuredImage = getLmsCourseFeaturedImage(course);

  return {
    tab: learnerStatus.tab,
    course: {
      id: String(course?.id ?? enrolment?.id ?? "unknown-course"),
      enrolmentId: enrolment?.id ? String(enrolment.id) : undefined,
      courseId: course?.id ? String(course.id) : undefined,
      title:
        course?.title ??
        course?.name ??
        enrolment?.course_name ??
        enrolment?.name ??
        t("courses"),
      category: {
        id: getLmsCourseCategoryId(course?.category) || undefined,
        name: course?.category?.name ?? t("e_learning"),
      },
      language: selectedLanguage,
      languageCode: getLanguageCode(selectedLanguage),
      availableLanguageCodes: getAvailableLanguageCodes(
        course?.available_languages
      ),
      durationLabel: getAvailableDurationsLabel(
        course,
        enrolment?.duration_in_minutes
      ),
      imageUrl:
        featuredImage?.thumbnailViewLink ?? featuredImage?.viewLink
          ? getImageUrl(
              featuredImage.thumbnailViewLink ?? featuredImage.viewLink
            )
          : null,
      bottomStatusLabel: learnerStatus.label,
      bottomStatusTone: learnerStatus.tone,
      canDownloadCertificate:
        learnerStatus.tab === "completed" &&
        Boolean(enrolment?.id),
    },
  };
};

export const loadPaginatedEntity = async (
  entity: string,
  parametersToRender: Record<string, string | number>,
  offset = 0
) => {
  return new Promise<PaginatedResponse<any>>((resolve, reject) => {
    api.genericApiRequest({
      entity,
      method: "get",
      shouldNotImpersonate: true,
      parametersToRender: {
        offset,
        limit: PAGE_SIZE,
        ...parametersToRender,
      },
      successHandler: (res: any) => {
        resolve({
          count: Number(res?.data?.count ?? 0),
          results: res?.data?.results ?? [],
        });
      },
      failHandler: (message: any) => {
        reject(message ?? new Error("Request failed"));
      },
    });
  });
};

export const getManagerCourseEnrolledCount = (course: any): number => {
  const countCandidate =
    course?.enrolled_users_count ??
    course?.enrolment_count ??
    course?.enrollment_count ??
    course?.participants_count ??
    course?.participant_count ??
    0;

  const parsedCount = Number(countCandidate);
  return Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : 0;
};

export const mapManagerCourseToCardData = (
  course: any,
  t: (value: string, options?: any) => string,
  childrenSubjectId?: string
): LmsCourseCardData => {
  const enrolledUsersCount = getManagerCourseEnrolledCount(course);
  const normalizedCourseId =
    course?.id !== undefined && course?.id !== null ? String(course.id) : "";
  const featuredImage = getLmsCourseFeaturedImage(course);

  return {
    id: normalizedCourseId || "unknown-course",
    courseId: normalizedCourseId || undefined,
    title: getLmsDisplayText(course?.title ?? course?.name) || t("courses"),
    category: {
      id: getLmsCourseCategoryId(course?.category) || undefined,
      name:
        getLmsDisplayText(
          course?.category?.label ?? course?.category?.name ?? course?.category
        ) || "-",
    },
    availableLanguageCodes: getAvailableLanguageCodes(
      course?.available_languages
    ),
    durationLabel: getAvailableDurationsLabel(course),
    imageUrl:
      featuredImage?.thumbnailViewLink ?? featuredImage?.viewLink
        ? getImageUrl(
            featuredImage.thumbnailViewLink ?? featuredImage.viewLink,
            childrenSubjectId
          )
        : null,
    bottomStatusLabel:
      enrolledUsersCount > 0
        ? t("manager_enrolled_users_count", {
            count: enrolledUsersCount,
          })
        : undefined,
    bottomStatusTone: "neutral",
  };
};
